const getRootStyles = () => window.getComputedStyle(document.documentElement)

export const readCssVar = (styles, name, fallback = '') => {
  const value = styles.getPropertyValue(name).trim()
  return value || fallback
}

const parseHexColor = (color) => {
  const normalized = color.replace('#', '').trim()

  if (![3, 6].includes(normalized.length)) return null

  const hex = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16)
  }
}

const parseRgbColor = (color) => {
  const match = color.match(/^rgba?\(([^)]+)\)$/i)
  if (!match) return null

  const [r, g, b] = match[1]
    .split(',')
    .slice(0, 3)
    .map((part) => Number(part.trim()))

  if ([r, g, b].some((value) => Number.isNaN(value))) return null

  return { r, g, b }
}

const parseColor = (color) => {
  if (!color) return null

  const normalized = color.trim()

  if (normalized.startsWith('#')) return parseHexColor(normalized)
  if (normalized.startsWith('rgb')) return parseRgbColor(normalized)

  return null
}

export const withAlpha = (color, alpha, fallback = color) => {
  const parsed = parseColor(color)
  if (!parsed) return fallback

  return `rgba(${parsed.r}, ${parsed.g}, ${parsed.b}, ${alpha})`
}

export const getForecastChartTheme = () => {
  const styles = getRootStyles()

  return {
    textPrimary: readCssVar(styles, '--component-forecast-text', '#2a2b2d'),
    textMuted: readCssVar(styles, '--component-forecast-text-muted', '#565759'),
    textSubtle: readCssVar(styles, '--component-forecast-text-subtle', '#707070'),
    surface: readCssVar(styles, '--component-forecast-surface', '#ffffff'),
    surfaceAlt: readCssVar(styles, '--component-forecast-surface-alt', '#f6f6f6'),
    tooltipSurface: readCssVar(styles, '--component-forecast-dropdown-surface', '#ffffff'),
    divider: readCssVar(styles, '--component-forecast-divider', '#e5e5e5'),
    dividerSubtle: readCssVar(styles, '--component-forecast-divider-subtle', '#f2f2f2'),
    axis: readCssVar(styles, '--component-forecast-chart-axis', '#898a8b'),
    pointFill: readCssVar(styles, '--component-forecast-chart-point-fill', '#ffffff'),
    highlightBand: readCssVar(styles, '--component-forecast-chart-highlight-band', 'rgba(250, 250, 250, 1)'),
    valueWarm: readCssVar(styles, '--component-forecast-value-warm', '#de685b'),
    valueCool: readCssVar(styles, '--component-forecast-value-cool', '#6bb8dd'),
    temperatureLine: readCssVar(styles, '--component-forecast-chart-temperature-line', '#c22820'),
    temperatureFill: readCssVar(styles, '--component-forecast-chart-temperature-fill-end', 'rgba(197, 67, 60, .2)'),
    hourlyTemperatureLine: readCssVar(styles, '--component-forecast-hourly-temperature-line', '#ffcc01'),
    hourlyTemperatureFill: readCssVar(styles, '--component-forecast-hourly-temperature-fill', '#fff5d3'),
    rainLine: readCssVar(styles, '--component-forecast-chart-rain-line', '#0080cd'),
    rainFill: readCssVar(styles, '--component-forecast-chart-rain-fill-end', 'rgba(0, 128, 205, .2)'),
    windLine: readCssVar(styles, '--component-forecast-chart-wind-line', '#b3c04a'),
    windFill: readCssVar(styles, '--component-forecast-chart-wind-fill-end', 'rgba(179, 192, 74, .2)'),
    hourlyWindLine: readCssVar(styles, '--component-forecast-hourly-wind-line', '#aebc3d'),
    hourlyWindFill: readCssVar(styles, '--component-forecast-hourly-wind-fill', '#edf0d6'),
    humidityLine: readCssVar(styles, '--component-forecast-chart-humidity-line', '#005381'),
    humidityFill: readCssVar(styles, '--component-forecast-chart-humidity-fill-end', 'rgba(0, 83, 129, .2)'),
    hourlyHumidityLine: readCssVar(styles, '--component-forecast-hourly-humidity-line', '#00b5e8'),
    hourlyHumidityFill: readCssVar(styles, '--component-forecast-hourly-humidity-fill', '#caeef9'),
    pressureLine: readCssVar(styles, '--component-forecast-chart-pressure-line', '#c3d060'),
    pressureFill: readCssVar(styles, '--component-forecast-chart-pressure-fill', '#f1f4dd'),
    mosquitoLine: readCssVar(styles, '--component-forecast-chart-mosquito-line', '#b7b6b6'),
    mosquitoFill: readCssVar(styles, '--component-forecast-chart-mosquito-fill', '#e5e5e5'),
    iuvLine: readCssVar(styles, '--component-forecast-chart-iuv-line', '#fcc000'),
    iuvFill: readCssVar(styles, '--component-forecast-chart-iuv-fill', '#fef4d4'),
    fluLine: readCssVar(styles, '--component-forecast-chart-flu-line', '#ef8282'),
    fluFill: readCssVar(styles, '--component-forecast-chart-flu-fill', '#f0d8d8'),
    skinLine: readCssVar(styles, '--component-forecast-chart-skin-line', '#ef8282'),
    skinFill: readCssVar(styles, '--component-forecast-chart-skin-fill', '#f0d8d8')
  }
}

export const getLegacyChartPalette = () => {
  const styles = getRootStyles()

  const legacy = {
    surface: readCssVar(styles, '--component-legacy-chart-surface', '#fafafa'),
    tooltipSurface: readCssVar(styles, '--component-legacy-chart-tooltip-surface', '#ffffff'),
    tooltipText: readCssVar(styles, '--component-legacy-chart-tooltip-text', '#707070'),
    axis: readCssVar(styles, '--component-legacy-chart-axis', '#898a8b'),
    pointFill: readCssVar(styles, '--component-legacy-chart-point-fill', '#ffffff'),
    compareWarmLine: readCssVar(styles, '--component-legacy-chart-compare-warm-line', '#de685b'),
    compareWarmFill: readCssVar(styles, '--component-legacy-chart-compare-warm-fill', '#fcd6d6'),
    compareCoolLine: readCssVar(styles, '--component-legacy-chart-compare-cool-line', '#6bb8dd'),
    compareCoolFill: readCssVar(styles, '--component-legacy-chart-compare-cool-fill', '#dbf4ff'),
    temperatureLine: readCssVar(styles, '--component-legacy-chart-temperature-line', '#ff5722'),
    temperatureFill: readCssVar(styles, '--component-legacy-chart-temperature-fill', 'rgba(255, 87, 34, .21)'),
    rainLine: readCssVar(styles, '--component-legacy-chart-rain-line', '#0180cd'),
    rainFill: readCssVar(styles, '--component-legacy-chart-rain-fill', '#e5e5e5'),
    windLine: readCssVar(styles, '--component-legacy-chart-wind-line', '#aebc3d'),
    windFill: readCssVar(styles, '--component-legacy-chart-wind-fill', '#edf0d6'),
    humidityLine: readCssVar(styles, '--component-legacy-chart-humidity-line', '#00b5e8'),
    humidityFill: readCssVar(styles, '--component-legacy-chart-humidity-fill', '#caeef9'),
    pressureLine: readCssVar(styles, '--component-legacy-chart-pressure-line', '#c3d060'),
    pressureFill: readCssVar(styles, '--component-legacy-chart-pressure-fill', '#f1f4dd')
  }

  return {
    surface: legacy.surface,
    tooltipSurface: legacy.tooltipSurface,
    tooltipText: legacy.tooltipText,
    valueText: readCssVar(styles, '--component-forecast-text', '#2a2b2d'),
    axis: legacy.axis,
    pointFill: legacy.pointFill,
    rainFill: legacy.rainFill,
    temperature: {
      double: {
        lineColor: [legacy.compareWarmLine, legacy.compareCoolLine],
        backgroundColor: [legacy.compareWarmFill, legacy.compareCoolFill]
      },
      single: {
        lineColor: [legacy.temperatureLine],
        backgroundColor: [legacy.temperatureFill]
      }
    },
    rain: {
      single: {
        lineColor: [legacy.rainLine],
        backgroundColor: [legacy.rainLine]
      }
    },
    wind: {
      double: {
        lineColor: [legacy.compareCoolLine, legacy.windLine],
        backgroundColor: [legacy.compareCoolFill, legacy.windFill]
      },
      single: {
        lineColor: [legacy.windLine],
        backgroundColor: [legacy.windFill]
      }
    },
    humidity: {
      double: {
        lineColor: [legacy.compareWarmLine, legacy.compareCoolLine],
        backgroundColor: [legacy.compareWarmFill, legacy.compareCoolFill]
      },
      single: {
        lineColor: [legacy.humidityLine],
        backgroundColor: [legacy.humidityFill]
      }
    },
    pressure: {
      single: {
        lineColor: [legacy.pressureLine],
        backgroundColor: [legacy.pressureFill]
      }
    }
  }
}

export const getWindTheme = () => {
  const styles = getRootStyles()

  return {
    compassStroke: readCssVar(styles, '--component-wind-compass-stroke', '#707070'),
    chartSurface: readCssVar(styles, '--component-wind-chart-surface', '#ffffff'),
    chartAxis: readCssVar(styles, '--component-wind-chart-axis', '#707070'),
    chartGrid: readCssVar(styles, '--component-wind-chart-grid', '#e5e5e5'),
    chartWind: readCssVar(styles, '--component-wind-chart-line', '#27a918'),
    chartGust: readCssVar(styles, '--component-wind-chart-gust', '#ff7300')
  }
}

export const getMapTheme = () => {
  const styles = getRootStyles()

  return {
    locateBorder: readCssVar(styles, '--component-map-locate-border', '#ffffff'),
    locateFill: readCssVar(styles, '--component-map-locate-fill', '#3388ff'),
    windOverlay: readCssVar(styles, '--component-map-wind-overlay', '#ffffff'),
    windOverlayMuted: readCssVar(styles, '--component-map-wind-overlay-muted', '#666666'),
    fireRisk: [
      readCssVar(styles, '--component-map-legend-fire-risk-1', '#00ff00'),
      readCssVar(styles, '--component-map-legend-fire-risk-2', '#aaff7f'),
      readCssVar(styles, '--component-map-legend-fire-risk-3', '#ffc820'),
      readCssVar(styles, '--component-map-legend-fire-risk-4', '#ff0000'),
      readCssVar(styles, '--component-map-legend-fire-risk-5', '#750013')
    ],
    floodRisk: [
      readCssVar(styles, '--component-map-legend-flood-risk-1', '#f0e12b 33%'),
      readCssVar(styles, '--component-map-legend-flood-risk-2', '#f2b727 33% 66%'),
      readCssVar(styles, '--component-map-legend-flood-risk-3', '#c53a38 66%')
    ]
  }
}
